import { AutocompleteOrganizations } from '@components/autocomplete/AutocompleteOrganizations';
import { AutocompleteVendors } from '@components/autocomplete/AutocompleteVendors';
import { AutocompleteVenues } from '@components/autocomplete/AutocompleteVenues';
import { ReviewTextListField, useFormContext } from '@goodfynd/forms';
import { FlexColumn, FlexColumns, FlexView } from '@goodfynd/ui';
import { ChipContainer } from '@goodfynd/ui';
import type { z } from 'zod';

import type { OptionSchema } from 'components/forms/validationSchemas';

import type { TopicsFields } from './types';

export function StepTopics() {
  const { setValue, watch } = useFormContext<TopicsFields>();

  const organizationAlerts = watch('organizationAlerts') ?? [];
  const vendorAlerts = watch('vendorAlerts') ?? [];
  const venueAlerts = watch('venueAlerts') ?? [];

  const renderTopicsEntity = (
    autocomplete: React.ReactNode,
    subscriptions: z.infer<typeof OptionSchema>[],
    onChange: (options: z.infer<typeof OptionSchema>[]) => void,
    onDelete: (value: number | string) => void
  ) => {
    return (
      <FlexView direction="column" gap={8} width="100%">
        {autocomplete}

        <ChipContainer
          onChange={onChange}
          onDelete={onDelete}
          values={subscriptions}
        />
      </FlexView>
    );
  };

  return (
    <FlexView direction="column" gap={24} margin="0 0 24px" width="100%">
      {renderTopicsEntity(
        <AutocompleteOrganizations
          css={{ padding: '16px 0 0', width: '100%' }}
          label="Get organizational alerts for"
          onChange={(option) => {
            if (
              option &&
              !organizationAlerts.some((alert) => alert.value == option.value)
            ) {
              setValue('organizationAlerts', [...organizationAlerts, option]);
            }
          }}
        />,
        organizationAlerts,
        (options) => {
          setValue('organizationAlerts', options);
        },
        (value) => {
          setValue(
            'organizationAlerts',
            organizationAlerts.filter((alert) => alert.value !== value)
          );
        }
      )}

      {renderTopicsEntity(
        <AutocompleteVendors
          css={{ padding: '16px 0 0', width: '100%' }}
          label="Get vendor alerts for"
          onChange={(option) => {
            if (
              option &&
              !vendorAlerts.some((alert) => alert.value == option.value)
            ) {
              setValue('vendorAlerts', [...vendorAlerts, option]);
            }
          }}
        />,
        vendorAlerts,
        (options) => {
          setValue('vendorAlerts', options);
        },
        (value) => {
          setValue(
            'vendorAlerts',
            vendorAlerts.filter((alert) => alert.value !== value)
          );
        }
      )}

      {renderTopicsEntity(
        <AutocompleteVenues
          css={{ padding: '16px 0 0', width: '100%' }}
          label="Get venue alerts for"
          onChange={(option) => {
            if (
              option &&
              !venueAlerts.some((alert) => alert.value == option.value)
            ) {
              setValue('venueAlerts', [...venueAlerts, option]);
            }
          }}
        />,
        venueAlerts,
        (options) => {
          setValue('venueAlerts', options);
        },
        (value) => {
          setValue(
            'venueAlerts',
            venueAlerts.filter((alert) => alert.value !== value)
          );
        }
      )}
    </FlexView>
  );
}

export function StepTopicsSummary() {
  const { getValues } = useFormContext<TopicsFields>();
  const { organizationAlerts, vendorAlerts, venueAlerts } = getValues();
  return (
    <FlexColumns columns={2} gap={16} span="stretch" wrap="nowrap">
      <FlexColumn>
        <ReviewTextListField
          label="Organization alerts"
          values={organizationAlerts.map((alert) => alert.label)}
        />
        <ReviewTextListField
          label="Vendor alerts"
          values={vendorAlerts.map((alert) => alert.label)}
        />
        <ReviewTextListField
          label="Venue alerts"
          values={venueAlerts.map((alert) => alert.label)}
        />
      </FlexColumn>
    </FlexColumns>
  );
}
